/**
 * The site as it actually is, rather than as the research outline imagines it.
 *
 * WHY THIS SCREEN EXISTS. A page's URL used to be derived from the subject tree
 * on every render, so it could not drift — it was recomputed from one source
 * every time. Storing the path on the page buys stability (reorganising
 * research stops republishing) and pays for it in drift, and nothing else in
 * the product would ever show you the site as a whole. This is the other half
 * of that trade, and it is the only place a mis-filed page becomes visible.
 *
 * DERIVED, SO IT CANNOT LIE. The tree is a grouping over what the pages say.
 * There is nothing to keep in sync and no branch can appear that no page is
 * filed under.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { withReturnTo } from '../common/returnPath';
import api from '../../services/api';
import {
  buildStructure,
  rootPages,
  suspectSiblings,
} from './siteStructure';
import type { StructureNode, StructurePage } from './siteStructure';
import PathChips from '../common/PathChips';
import type { SiteStructure } from '../../types';

interface Props {
  siteId: number;
}

export default function SiteStructurePanel({ siteId }: Props) {
  const [data, setData] = useState<SiteStructure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Which branch is being re-filed, and where to. A BRANCH at a time, because
  // what this screen surfaces is a branch in the wrong place and every page
  // under it shares the mistake.
  const [moving, setMoving] = useState<StructureNode | null>(null);
  const [moveTo, setMoveTo] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [creatingIndex, setCreatingIndex] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSiteStructure(siteId);
      setData(res);
      // Roots open on arrival. A tree that starts fully collapsed shows a list
      // of names and hides the thing people came for — where the pages are.
      setOpen(
        new Set(
          buildStructure(res.pages).map((n) => n.path.join('/')),
        ),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the structure');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const startMove = (node: StructureNode) => {
    setMoving(node);
    setMoveTo(node.path);
    setNotice(null);
  };

  const commitMove = async () => {
    if (!moving) return;
    setSaving(true);
    try {
      const res = await api.moveSiteStructure(siteId, moving.path, moveTo);
      setMoving(null);
      setNotice(
        res.moved === 0
          ? 'Nothing moved - that is already the path.'
          : `Moved ${res.moved} page${res.moved === 1 ? '' : 's'} to /${res.to_path.join('/')}/` +
            (res.published_moved
              ? ` · ${res.published_moved} were published, so their old URLs are now dead.`
              : ''),
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'The move failed');
    } finally {
      setSaving(false);
    }
  };

  const createIndex = async (node: StructureNode) => {
    setCreatingIndex(node.path.join('/'));
    setNotice(null);
    try {
      await api.createSectionIndex(siteId, node.path);
      setNotice(`Created the page for /${node.path.join('/')}/ - open it to write it.`);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Could not create the section page');
    } finally {
      setCreatingIndex(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
        {error}
      </p>
    );
  }
  if (!data) return null;

  const tree = buildStructure(data.pages);
  const loose = rootPages(data.pages);
  const suspects = suspectSiblings(tree);
  const suspectPaths = new Set(suspects.flat());

  if (!data.pages.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No pages are pointed at this site yet. Once you make some, their publish
        paths draw the shape of the site here.
      </p>
    );
  }

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div>
      {!data.nested && (
        // Saying this once, at the top, rather than on every row. The paths are
        // real and filed; this site type just does not put them in the URL.
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded px-3 py-2">
          This site publishes every page at the root, so the paths below file
          the pages but do not appear in their URLs. Move the site to Hugo or
          Next.js and this becomes the URL structure.
        </p>
      )}

      {suspects.length > 0 && (
        <div className="mb-3 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
          <p className="font-medium">
            {suspects.length === 1
              ? 'Two branches look like the same name typed twice.'
              : `${suspects.length} pairs of branches look like the same name typed twice.`}
          </p>
          <ul className="mt-1 space-y-0.5">
            {suspects.map(([a, b]) => (
              <li key={`${a}|${b}`} className="font-mono">
                /{a}/ · /{b}/
              </li>
            ))}
          </ul>
          <p className="mt-1">
            They are different URLs. Open a page in either and fix its publish
            path to merge them.
          </p>
        </div>
      )}

      {notice && (
        <p className="mb-3 text-xs text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded px-3 py-2">
          {notice}
        </p>
      )}

      {moving && (
        <div className="mb-3 p-3 border border-blue-300 dark:border-blue-700 rounded-md bg-blue-50/50 dark:bg-blue-900/20">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Move <code className="font-mono">/{moving.path.join('/')}/</code> and
            everything under it
          </p>
          <p className="mt-0.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
            {moving.totalPages} page{moving.totalPages === 1 ? '' : 's'}
            {moving.deployedPages > 0 && (
              <>
                {' · '}
                <span className="text-amber-700 dark:text-amber-300">
                  {moving.deployedPages} published - moving them breaks every
                  link to their current URLs
                </span>
              </>
            )}
          </p>
          <div className="max-w-xl">
            <PathChips
              segments={moveTo}
              onChange={setMoveTo}
              managedSiteId={siteId}
              nested={data.nested}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={commitMove}
              disabled={saving || moveTo.join('/') === moving.path.join('/')}
              className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Moving…' : `Move ${moving.totalPages} page(s)`}
            </button>
            <button
              type="button"
              onClick={() => setMoving(null)}
              className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ul className="text-sm">
        {tree.map((node) => (
          <Branch
            key={node.path.join('/')}
            node={node}
            depth={0}
            open={open}
            onToggle={toggle}
            suspectPaths={suspectPaths}
            onMove={startMove}
            onCreateIndex={createIndex}
            creatingIndex={creatingIndex}
            nested={data.nested}
          />
        ))}
      </ul>

      {loose.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            At the site root
          </p>
          <ul className="mt-1">
            {loose.map((p) => (
              <PageRow key={p.id} page={p} depth={0} />
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        {data.pages.length} page{data.pages.length === 1 ? '' : 's'} ·{' '}
        {data.pages.filter((p) => p.last_deployed_at).length} published
      </p>
    </div>
  );
}

function Branch({
  node,
  depth,
  open,
  onToggle,
  suspectPaths,
  onMove,
  onCreateIndex,
  creatingIndex,
  nested,
}: {
  node: StructureNode;
  depth: number;
  open: Set<string>;
  onToggle: (key: string) => void;
  suspectPaths: Set<string>;
  onMove: (node: StructureNode) => void;
  onCreateIndex: (node: StructureNode) => void;
  creatingIndex: string | null;
  nested: boolean;
}) {
  const here = useLocation();
  const key = node.path.join('/');
  const isOpen = open.has(key);
  const hasChildren = node.children.length > 0 || node.pages.length > 0;

  return (
    <li>
      <div
        style={{ paddingLeft: depth * 16 + 4 }}
        className="group flex items-center gap-1.5 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50"
      >
        <button
          type="button"
          onClick={() => onToggle(key)}
          aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
          className={`p-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 ${
            hasChildren ? '' : 'invisible pointer-events-none'
          }`}
        >
          <svg
            className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M7 5l6 5-6 5V5z" />
          </svg>
        </button>
        <span
          className={`font-mono ${
            suspectPaths.has(key)
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-gray-800 dark:text-gray-200'
          }`}
        >
          {node.name}/
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {node.totalPages} page{node.totalPages === 1 ? '' : 's'}
          {/* Published vs merely existing. A tree that draws unpublished pages
              as though they were live is how a link graph manufactures 404s. */}
          {node.deployedPages > 0 && ` · ${node.deployedPages} live`}
        </span>
        {/* Faint until the row is under the pointer. Moving a branch rewrites
            real URLs, so the verb should be findable without being the loudest
            thing on a row whose job is to be read. */}
        {/* THE SECTION'S OWN PAGE. Present, it is a link; absent, an offer.
            Never created automatically: forty sections would mean forty
            near-empty pages going live, which is a doorway-page factory. */}
        {node.index ? (
          <Link
            to={withReturnTo(
              `/page-builder/${node.index.id}`,
              `${here.pathname}${here.search}`,
              'the site structure',
            )}
            className="ml-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline shrink-0"
            title="This section's own page"
          >
            index{node.index.last_deployed_at ? '' : ' (draft)'}
          </Link>
        ) : (
          nested && (
            <button
              type="button"
              onClick={() => onCreateIndex(node)}
              disabled={creatingIndex !== null}
              title="Create a page for this section itself"
              className="ml-1 px-1.5 py-0.5 text-[11px] rounded text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40"
            >
              {creatingIndex === node.path.join('/') ? 'Creating…' : '+ index'}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onMove(node)}
          className="ml-1 px-1.5 py-0.5 text-[11px] rounded text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-700 dark:hover:text-gray-200"
        >
          Move…
        </button>
      </div>

      {isOpen && (
        <ul>
          {node.children.map((child) => (
            <Branch
              key={child.path.join('/')}
              node={child}
              depth={depth + 1}
              open={open}
              onToggle={onToggle}
              suspectPaths={suspectPaths}
              onMove={onMove}
              onCreateIndex={onCreateIndex}
              creatingIndex={creatingIndex}
              nested={nested}
            />
          ))}
          {node.pages.map((p) => (
            <PageRow key={p.id} page={p} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function PageRow({ page, depth }: { page: StructurePage; depth: number }) {
  const here = useLocation();
  return (
    <li
      style={{ paddingLeft: depth * 16 + 26 }}
      className="flex items-center gap-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <Link
        to={withReturnTo(
          `/page-builder/${page.id}`,
          `${here.pathname}${here.search}`,
          'the site structure',
        )}
        className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
        title={page.title ?? undefined}
      >
        {page.slug || `page-${page.id}`}
      </Link>
      {page.last_deployed_at ? (
        <span className="text-[11px] text-green-700 dark:text-green-400">live</span>
      ) : (
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          not published
        </span>
      )}
      {/* Which subject it came FROM. The point of this screen is that the site
          and the research outline are allowed to differ, so both are shown. */}
      {page.subject_name && (
        <span className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
          {page.subject_name}
        </span>
      )}
    </li>
  );
}
